export interface PersonBetter {
    seed_key: string;
    first: string;
    middle?: string;
    last: string;
    dob?: Date;
    profile_image_key?: string;
    profile_url?: string;
    relationships?: Relationship[];
    life_events?: LifeEvent[];
    notes?: string | null;
}

export enum LifeEventType {
    Birth = 'Birth',
    Death = 'Death',
    Marriage = 'Marriage',
    Divorce = 'Divorce',
    Graduation = 'Graduation',
    CareerChange = 'Career Change',
    Relocation = 'Relocation',
    BirthOfChild = 'Birth of Child',
    Retirement = 'Retirement',
    MilitaryService = 'Military Service',
    AwardRecognition = 'Award/Recognition',
    Other = 'Other'
}

export const LifeEventTypes = [
    LifeEventType.Birth,
    LifeEventType.Death,
    LifeEventType.Marriage,
    LifeEventType.Divorce,
    LifeEventType.Graduation,
    LifeEventType.CareerChange,
    LifeEventType.Relocation,
    LifeEventType.BirthOfChild,
    LifeEventType.Retirement,
    LifeEventType.MilitaryService,
    LifeEventType.AwardRecognition,
    LifeEventType.Other,
];

export interface LifeEvent {
    type: '' | LifeEventType;
    date: Date | null;
    description?: string;
    isLocked?: boolean;
}

export enum RelationshipType {
    IsChildOf = 'Child',
    IsParentOf = 'Parent',
    IsGrandparentOf = 'Grandparent',
    IsGrandchildOf = 'Grandchild',
    IsSiblingOf = 'Sibling',
    IsSpouseOf = 'Spouse',
    IsFriendOf = 'Friend',
    IsAuntOf = 'Aunt',
    IsUncleOf = 'Uncle',
    IsNieceOf = 'Niece',
    IsNephewOf = 'Nephew',
    IsCousinOf = 'Cousin',
    IsInLawOf = 'In-Law',
    IsStepParentOf = 'Step-Parent',
    IsStepChildOf = 'Step-Child',
    IsStepSiblingOf = 'Step-Sibling',
    IsPartnerOf = 'Partner',
    IsOtherOf = 'Other'
}

export const RelationshipTypes = [
    RelationshipType.IsChildOf,
    RelationshipType.IsParentOf,
    RelationshipType.IsGrandparentOf,
    RelationshipType.IsGrandchildOf,
    RelationshipType.IsSiblingOf,
    RelationshipType.IsSpouseOf,
    RelationshipType.IsFriendOf,
    RelationshipType.IsAuntOf,
    RelationshipType.IsUncleOf,
    RelationshipType.IsNieceOf,
    RelationshipType.IsNephewOf,
    RelationshipType.IsCousinOf,
    RelationshipType.IsInLawOf,
    RelationshipType.IsStepParentOf,
    RelationshipType.IsStepChildOf,
    RelationshipType.IsStepSiblingOf,
    RelationshipType.IsPartnerOf,
    RelationshipType.IsOtherOf
];


export interface Relationship {
    type: RelationshipType | null;
    name: string | null;
    person?: PersonBetter;
    custom_type?: string; // For custom relationship types
}

/**
 * Relationship type conversion utilities
 * 
 * These functions help standardize relationship types between frontend and backend formats:
 * 
 * Frontend formats:
 * - IsXxxOf (enum values like IsChildOf, IsParentOf)
 * - Display format (Child, Parent)
 * 
 * Backend format:
 * - UPPERCASE (CHILD, PARENT)
 */

/**
 * Standardizes a relationship type to the frontend enum format (IsXxxOf)
 * 
 * @param relationshipType - The relationship type in any format
 * @returns The standardized relationship type in IsXxxOf format
 */
export const standardizeRelationshipType = (relationshipType: string | null): RelationshipType | null => {
    if (!relationshipType) return null;
    
    // Already in IsXxxOf format
    if (relationshipType.startsWith('Is') && relationshipType.includes('Of') && relationshipType.length > 5) {
        return relationshipType as RelationshipType;
    }
    
    // Handle legacy HasXxx format
    if (relationshipType.startsWith('Has') && relationshipType.length > 3) {
        const baseType = relationshipType.substring(3); // Remove 'Has'
        const newFormat = 'Is' + baseType + 'Of';
        // Check if this format exists in the enum
        if (Object.values(RelationshipType).includes(newFormat as any)) {
            return newFormat as RelationshipType;
        }
    }
    
    // Convert from UPPERCASE format (e.g., CHILD)
    const baseType = relationshipType;
    
    // Convert to Title Case (e.g., CHILD -> Child)
    const titleCase = baseType.charAt(0).toUpperCase() + baseType.slice(1).toLowerCase();
    
    // Find matching RelationshipType
    for (const [key, value] of Object.entries(RelationshipType)) {
        if (value === titleCase) {
            return key as RelationshipType;
        }
    }
    
    // Default to IsOtherOf if no match found
    console.warn(`No standard relationship type found for: ${relationshipType}, defaulting to IsOtherOf`);
    return RelationshipType.IsOtherOf;
};

/**
 * Converts a relationship type to the backend format (UPPERCASE)
 * 
 * @param relationshipType - The relationship type in frontend format
 * @returns The relationship type in backend format (UPPERCASE)
 */
export const toBackendRelationshipType = (relationshipType: RelationshipType | string | null): string | null => {
    if (!relationshipType) return null;
    
    // Convert IsXxxOf format to XXX format
    let baseType = relationshipType;
    
    // Handle new IsXxxOf format
    if (relationshipType.startsWith('Is') && relationshipType.endsWith('Of') && relationshipType.length > 5) {
        // Extract the middle part (e.g., from 'IsChildOf' to 'Child')
        baseType = relationshipType.slice(2, -2); // Remove 'Is' and 'Of'
    }
    // Handle legacy HasXxx format for backward compatibility
    else if (relationshipType.startsWith('Has') && relationshipType.length > 3) {
        baseType = relationshipType.slice(3);
    }
    
    // Return in uppercase
    return baseType.toUpperCase();
};

/**
 * Formats a relationship type for display
 * 
 * @param relationshipType - The relationship type in any format
 * @returns A properly formatted relationship type for display (e.g., "Child", "Parent", etc.)
 */
export const formatRelationshipTypeForDisplay = (relationshipType: string | null): string => {
    if (!relationshipType) return '';
    
    // If it's in IsXxxOf format (e.g., "IsChildOf")
    if (relationshipType.startsWith('Is') && relationshipType.endsWith('Of') && relationshipType.length > 5) {
        return relationshipType.slice(2, -2); // Remove the "Is" prefix and "Of" suffix
    }
    
    // Handle legacy HasXxx format for backward compatibility
    if (relationshipType.startsWith('Has') && relationshipType.length > 3) {
        return relationshipType.slice(3); // Remove the "Has" prefix
    }
    
    // If it's in all caps (e.g., "CHILD")
    if (relationshipType === relationshipType.toUpperCase()) {
        return relationshipType.charAt(0) + relationshipType.slice(1).toLowerCase();
    }
    
    // If it's already in the correct format (first letter capitalized, rest lowercase)
    if (relationshipType.length > 1 && 
        relationshipType[0] === relationshipType[0].toUpperCase() && 
        relationshipType.slice(1) === relationshipType.slice(1).toLowerCase()) {
        return relationshipType;
    }
    
    // Default case - just return as is
    return relationshipType;
}